import { useCallback, useEffect, useRef, useState } from "react";

// Browser geolocation wrapper. Live watch for the "my location" dot + a one-shot
// capture used by the admin GPS calibration.
export function useGeolocation() {
  const [status, setStatus] = useState("idle"); // idle | searching | active | denied | unavailable | error
  const [coords, setCoords] = useState(null);    // { lat, lon, accuracy }
  const [error, setError] = useState("");
  const watchId = useRef(null);

  const stop = useCallback(() => {
    if (watchId.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    }
    setStatus("idle");
  }, []);

  const start = useCallback(() => {
    if (!("geolocation" in navigator)) { setStatus("unavailable"); return; }
    setStatus("searching");
    setError("");
    watchId.current = navigator.geolocation.watchPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setStatus("active");
      },
      (err) => {
        if (err.code === 1) setStatus("denied");
        else { setStatus("error"); setError(err.message || "Eroare locație"); }
      },
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 20000 }
    );
  }, []);

  // one-shot high-accuracy reading (used for calibration capture)
  const captureOnce = useCallback(
    () =>
      new Promise((resolve, reject) => {
        if (!("geolocation" in navigator)) { reject(new Error("GPS indisponibil")); return; }
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy }),
          (err) => reject(err),
          { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 }
        );
      }),
    []
  );

  useEffect(() => () => stop(), [stop]);

  return { status, coords, error, start, stop, captureOnce };
}
